import { Component, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import {
    UserIcon,
    InboxIcon,
    Invoice01Icon,
    TaskDone01Icon,
    CheckmarkCircle01Icon,
    Search01Icon,
    Add01Icon
} from '@hugeicons/core-free-icons';
import { DashboardService, DashboardResponse } from '../../core/services/dashboard.service';
import { CaseEnquiryService } from '../../core/services/case-enquiry.service';
import { AuthService } from '../../core/services/auth.service';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { NoDataComponent } from '../../shared/components/no-data/no-data.component';
import { CaseEnquiryDetailsComponent } from '../case-enquiry/case-enquiry-details/case-enquiry-details.component';
import { Enquiry } from '../case-enquiry/case-enquiry.component';

interface PhaseStatus {
    label: string;
    count: number;
    key: string;
}

interface PhaseColumn {
    title: string;
    icon: any;
    colorClass: string;
    statuses: PhaseStatus[];
    total: number;
}

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, CurrencyPipe, DatePipe, FormsModule, HugeiconsIconComponent, ModalComponent, NoDataComponent, CaseEnquiryDetailsComponent],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
    // Icons
    attorneyIcon = UserIcon;
    enquiryIcon = InboxIcon;
    offerIcon = Invoice01Icon;
    closeIcon = TaskDone01Icon;
    acceptedIcon = CheckmarkCircle01Icon;
    searchIcon = Search01Icon;
    addIcon = Add01Icon;

    // Phase data
    enquiryPhase!: PhaseColumn;
    bottomPhases: PhaseColumn[] = [];

    // Case search
    caseSearchQuery = '';
    showSearchResultsModal = false;
    searchResults: any[] = [];
    searchLoading = false;

    // Cases listing modal
    showCasesModal = false;
    casesModalTitle = '';
    casesModalData: any[] = [];
    casesModalLoading = false;
    casesModalType: 'case' | 'enquiry' = 'case';
    private casesModalCurrentKey = '';

    // Enquiry detail modal
    selectedEnquiry: Enquiry | null = null;

    private readonly ENQUIRY_KEYS = ['new_enquiry', 'accept_enquiry', 'reject_enquiry', 'total_enquiry'];

    private typeLabels: Record<string, string> = {
        new_enquiry: 'New/Pending Approval',
        accept_enquiry: 'Approved/Pending Details',
        pending_client_offer: 'Pending Client Offer',
        pending_client_acceptance: 'Pending Client Acceptance',
        accepted_pending_payment: 'Accepted/Pending Payment',
        pending_trust_funds: 'Pending Trust Funds',
        closed_archieved: 'Closed/Archived',
        release_of_lien: 'Pending Release of Lien',
        resolutions_accepted_pending_payment: 'Pending Hospital Payment'
    };

    // Loading
    loading = true;

    userName = '';

    get isAdmin(): boolean {
        return this.authService.getRole() === 'ADMIN';
    }

    get isAttorney(): boolean {
        return this.authService.getRole() === 'ATTORNEY';
    }

    constructor(
        private router: Router,
        private dashboardService: DashboardService,
        private authService: AuthService,
        private caseEnquiryService: CaseEnquiryService
    ) {}

    ngOnInit() {
        const user = this.authService.getCurrentUser();
        this.userName = user?.name || 'User';
        this.loadDashboardData();
    }

    loadDashboardData() {
        this.loading = true;

        this.dashboardService.getDashboard().subscribe({
            next: (data: DashboardResponse) => {
                // Enquiries - full width row
                this.enquiryPhase = {
                    title: 'Inquiries',
                    icon: this.enquiryIcon,
                    colorClass: 'phase-enquiry',
                    total: data.new_enquiry + data.accept_enquiry + (data.pending_client_offer || 0),
                    statuses: [
                        { label: 'New/Pending Approval', count: data.new_enquiry, key: 'new_enquiry' },
                        { label: 'Approved/Pending Details', count: data.accept_enquiry, key: 'accept_enquiry' },
                        { label: 'Pending Client Offer', count: data.pending_client_offer || 0, key: 'pending_client_offer' }
                    ]
                };

                // Offers, Resolutions, Closures - 3 column row
                if (this.isAttorney) {
                    this.bottomPhases = [
                        {
                            title: 'Offers',
                            icon: this.offerIcon,
                            colorClass: 'phase-offer',
                            total: (data.pending_client_acceptance || 0) + (data.accepted_pending_payment || 0),
                            statuses: [
                                { label: 'Pending Client Acceptance', count: data.pending_client_acceptance || 0, key: 'pending_client_acceptance' },
                                { label: 'Patient Pending Payment', count: data.accepted_pending_payment || 0, key: 'accepted_pending_payment' }
                            ]
                        },
                        {
                            title: 'Resolutions',
                            icon: this.acceptedIcon,
                            colorClass: 'phase-resolution',
                            total: (data.release_of_lien || 0) + (data.pending_trust_funds || 0),
                            statuses: [
                                { label: 'Pending Release of Lien', count: data.release_of_lien || 0, key: 'release_of_lien' },
                                { label: 'Pending Trust Funds', count: data.pending_trust_funds || 0, key: 'pending_trust_funds' }
                            ]
                        },
                        {
                            title: 'Closures',
                            icon: this.closeIcon,
                            colorClass: 'phase-closure',
                            total: data.closed_archieved || 0,
                            statuses: [
                                { label: 'Closed/Archived', count: data.closed_archieved || 0, key: 'closed_archieved' }
                            ]
                        }
                    ];
                } else {
                    this.bottomPhases = [
                        {
                            title: 'Offers',
                            icon: this.offerIcon,
                            colorClass: 'phase-offer',
                            total: (data.pending_client_acceptance || 0) + (data.accepted_pending_payment || 0),
                            statuses: [
                                { label: 'Pending Client Acceptance', count: data.pending_client_acceptance || 0, key: 'pending_client_acceptance' },
                                { label: 'Patient Pending Payment', count: data.accepted_pending_payment || 0, key: 'accepted_pending_payment' }
                            ]
                        },
                        {
                            title: 'Resolutions',
                            icon: this.acceptedIcon,
                            colorClass: 'phase-resolution',
                            total: (data.Resolutions_accepted_pending_payment || 0) + (data.release_of_lien || 0),
                            statuses: [
                                { label: 'Hospital Pending Payment', count: data.Resolutions_accepted_pending_payment || 0, key: 'resolutions_accepted_pending_payment' },
                                { label: 'Pending Release of Lien', count: data.release_of_lien || 0, key: 'release_of_lien' }
                            ]
                        },
                        {
                            title: 'Closures',
                            icon: this.closeIcon,
                            colorClass: 'phase-closure',
                            total: (data.pending_trust_funds || 0) + (data.closed_archieved || 0),
                            statuses: [
                                { label: 'Pending Trust Funds', count: data.pending_trust_funds || 0, key: 'pending_trust_funds' },
                                { label: 'Closed/Archived', count: data.closed_archieved || 0, key: 'closed_archieved' }
                            ]
                        }
                    ];
                }

                this.loading = false;
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    private readonly progressLabels: Record<number, string> = {
        1: 'Submitted',
        2: 'Offer Generated',
        3: 'Offer Accepted',
        4: 'Patient Payment',
        5: 'Hospital Payment',
        6: 'Release of Lien',
        7: 'Pending Trust Funds'
    };

    getProgressLabel(progress: number): string {
        return this.progressLabels[progress] || 'Unknown';
    }

    searchCase() {
        const query = this.caseSearchQuery.trim();
        if (!query) return;

        this.searchLoading = true;
        this.searchResults = [];

        this.dashboardService.searchCases(query).subscribe({
            next: (data: any) => {
                this.searchResults = Array.isArray(data) ? data : (data?.data || []);
                this.searchLoading = false;
                this.showSearchResultsModal = true;
            },
            error: () => {
                this.searchResults = [];
                this.searchLoading = false;
                this.showSearchResultsModal = true;
            }
        });
    }

    closeSearchResultsModal() {
        this.showSearchResultsModal = false;
        this.searchResults = [];
    }

    viewCase(caseId: number) {
        this.router.navigate(['/cases', caseId]);
    }

    navigateToAttorneys() {
        this.router.navigate(['/attorney']);
    }

    navigateToAddEnquiry() {
        this.showCasesModal = false;
        this.router.navigate(['/case-enquiry/add']);
    }

    openCasesModal(type: string) {
        this.casesModalTitle = this.typeLabels[type] || type;
        this.casesModalData = [];
        this.casesModalLoading = true;
        this.casesModalType = this.ENQUIRY_KEYS.includes(type) ? 'enquiry' : 'case';
        this.casesModalCurrentKey = type;

        this.dashboardService.getDashboardCasesData(type).subscribe({
            next: (data: any) => {
                this.casesModalData = Array.isArray(data) ? data : (data?.data || []);
                this.casesModalLoading = false;
                this.showCasesModal = true;
            },
            error: () => {
                this.casesModalData = [];
                this.casesModalLoading = false;
                this.showCasesModal = true;
            }
        });
    }

    closeCasesModal() {
        this.showCasesModal = false;
        this.casesModalData = [];
    }

    viewCaseFromModal(caseId: number) {
        this.router.navigate(['/cases', caseId]);
    }

    viewEnquiryFromModal(enquiryId: number) {
        this.showCasesModal = false;
        this.caseEnquiryService.getEnquiryById(enquiryId).subscribe({
            next: (detail: any) => {
                this.selectedEnquiry = {
                    id: detail.id ? `ENQ-${detail.id}` : '',
                    case_enquiry_id: detail.id,
                    client_name: [detail.client_first_name, detail.client_last_name].filter(Boolean).join(' '),
                    injury_type: detail.injury_type,
                    incident_date: detail.date_of_incident,
                    details: detail.details,
                    lien_details: detail.lien_details,
                    lien_amount: detail.lien_amount != null ? parseFloat(detail.lien_amount) : undefined,
                    settlement_status: detail.settlement_status,
                    settlement_amount: detail.settlement_amount,
                    has_loans: detail.settlement_loans != null ? !!detail.settlement_loans : undefined,
                    loan_amount: detail.loan_amount != null ? parseFloat(detail.loan_amount) : undefined,
                    total_medicals: detail.total_medicals != null ? parseFloat(detail.total_medicals) : undefined,
                    legal_fees: detail.legal_fees != null ? parseFloat(detail.legal_fees) : undefined,
                    costs: detail.costs != null ? parseFloat(detail.costs) : undefined,
                    status: detail.status,
                    inquiries_status: detail.inquiries_status,
                    attorney_name: detail.attorney?.attorney_name,
                    review_notes: detail.review_notes,
                    has_submission: detail.has_submission
                };
            }
        });
    }

    closeEnquiryDetailModal() {
        this.selectedEnquiry = null;
        this.showCasesModal = true;
    }

    onEnquiryStatusChanged() {
        this.selectedEnquiry = null;
        this.loadDashboardData();
        // Re-fetch the cases modal data to reflect the status change
        if (this.casesModalCurrentKey) {
            this.openCasesModal(this.casesModalCurrentKey);
        }
    }
}
